// @ts-strict-ignore
import { useCallback, useEffect, useState } from 'react';

import { Box, css, styled } from '@mui/material';
import { AnalyticEventAction } from 'analytics';
import { trackSearchUsageAnalyticsEvent } from 'analytics/events/search-usage';

import { observer } from 'mobx-react';

import { useNavigate } from 'react-router-dom';
import { NavigateFunction } from 'react-router-dom';

import { createTicketsTestSelectors } from 'selectors/create-tickets-page.selectors';

import { useStores } from 'mobx/hooks/useStores';

import PatientsFetcher, { SearchedPatient } from 'fetchers/PatientsFetcher';

import { useQuery } from 'hooks/useQuery';

import { HEADER_HEIGHT } from 'containers/Layout/Layout.constants';

import DoctorDetails from 'views/Dashboard/DoctorDetails';
import ConnectTicketsToCallEditModal from 'views/Modals/ConnectTicketsToCallEditModal';
import { PatientDetailsCard } from 'views/Patient/PatientDetails/PatientDetailsCard';
import { usePatientTicketOverview } from 'views/Patient/PatientMain/PatientTicketOverview';

import { PatientContextProvider } from 'components/Patient/PatientContextProvider';
import { OperatorCreateTicketForm } from 'components/Ticket/TicketForms/OperatorCreateTicketsForm';
import { TicketTypeDefaultFieldsProvider } from 'components/Ticket/TicketForms/TicketTypeDefaultFieldsProvider';
import TicketOverviewProvider, {
  ActionCallbacks
} from 'components/Ticket/TicketsContainers/TicketOverviewProvider';
import TicketsOverview from 'components/Ticket/TicketsContainers/TicketsOverview';
import { AdvancedSearchBar } from 'components/UIkit/atoms/AdvancedSearchBar/AdvancedSearchBar';
import { SelectedSearchedPatientBox } from 'components/UIkit/atoms/AdvancedSearchBar/SelectedSearchedPatientBox';

import './CreateTickets.scss';

interface ICreateTicketsPageProps {
  navigate: NavigateFunction;
}

const CreateTickets = observer(({ navigate }: ICreateTicketsPageProps) => {
  const { patientPageStore, userStore, ticketsStore, callsStore, alertsStore } = useStores();
  const [selectedPatientTime, setSelectedPatientTime] = useState<Date>();
  const [partialPatient, setPartialPatient] = useState(null);
  const queryParams = useQuery();
  const { patient: patientFromStore, overduePatients, ticketsCluster } = usePatientTicketOverview();
  const patient = patientFromStore || partialPatient;

  useEffect(() => {
    if (!alertsStore.alerts || alertsStore.alerts.length === 0) {
      alertsStore.getClinicianAlerts();
    }
  }, [alertsStore]);

  const isEmptyTickets =
    !ticketsCluster?.assignedTickets.length &&
    !ticketsCluster?.operatorTickets.length &&
    !ticketsCluster?.urgentSymptomsTickets.length &&
    !ticketsCluster?.otherSymptomTickets.length;

  const loadCreateTicketPatientData = useCallback(
    (patientId: number) => {
      return Promise.all([
        ticketsStore.searchTicketsForCreate(patientId),
        patientPageStore.loadSinglePatientFromServer(patientId)
      ]);
    },
    [patientPageStore, ticketsStore]
  );

  const onSelectedPatient = useCallback(
    async (selectedPatient: SearchedPatient, options?: { isKeyboardSource?: boolean }) => {
      trackSearchUsageAnalyticsEvent({
        action: AnalyticEventAction.Select,
        value: 'search result',
        source: options?.isKeyboardSource ? 'keyboard' : null
      });
      setSelectedPatientTime(new Date());
      // The retrieved patient might not have an id because he might not
      // be a patient on our system yet (EMR)
      if (selectedPatient.patientId) {
        // getting additional data from server
        // in order to display patients' tickets
        await loadCreateTicketPatientData(selectedPatient.patientId);
      } else if (selectedPatient.emrPatientId) {
        const emrPatient = await PatientsFetcher.fetchEmrPatient(selectedPatient.emrPatientId);
        setPartialPatient(emrPatient);
      } else {
        setPartialPatient(selectedPatient);
      }
    },
    [loadCreateTicketPatientData]
  );

  useEffect(
    function loadPatientFromUrl() {
      const loadPatientIfParamsExist = async () => {
        const queryParamsPatientId = queryParams.get('patientId');
        if (queryParamsPatientId) {
          navigate('/create-tickets', { replace: true });
          await loadCreateTicketPatientData(Number(queryParamsPatientId));
        }
      };

      loadPatientIfParamsExist();
    },
    [navigate, loadCreateTicketPatientData, onSelectedPatient, patientPageStore, queryParams]
  );

  const resetSelectedPatient = () => {
    patientPageStore.clearData();
    setPartialPatient(null);
  };

  const handleCreateTicketsSuccess = () => resetSelectedPatient();

  const updateActionCallback = useCallback(() => {
    if (patient) {
      patientPageStore.loadSinglePatientFromServer(patient.id);
    }
  }, [patientPageStore, patient]);

  const onTicketActionCallback = useCallback(() => {
    if (patient) {
      ticketsStore.searchTicketsForCreate(patient.id);
    }
  }, [patient, ticketsStore]);

  const createTicketsSync = useCallback(() => {
    onTicketActionCallback();
    updateActionCallback();
  }, [onTicketActionCallback, updateActionCallback]);

  const handleResolve = useCallback(
    (_ticketIds: number[], navigationPath?: string) => {
      if (navigationPath) {
        navigate(navigationPath, { replace: true });
        return;
      }
      createTicketsSync();
    },
    [createTicketsSync, navigate]
  );

  const handleClose = useCallback(
    (_ticketIds: number[], navigationPath?: string) => {
      if (navigationPath) {
        navigate(navigationPath, { replace: true });
        return;
      }
      createTicketsSync();
    },
    [createTicketsSync, navigate]
  );

  const actionCallbacks: ActionCallbacks = {
    onAssign: onTicketActionCallback,
    onEditTicket: createTicketsSync,
    onResolve: handleResolve,
    onClose: handleClose,
    onDeleteTicket: onTicketActionCallback,
    onRequestReport: updateActionCallback,
    onSnooze: updateActionCallback
  };

  return (
    <div
      className="animated fadeIn limited-width dashboard-view create-tickets-page"
      data-test-hook={createTicketsTestSelectors.page}
    >
      <Box mb={40}>
        <DoctorDetails doctor={userStore.currentDoctor} />
      </Box>

      <Box mb={20}>
        {patient ? (
          <SelectedSearchedPatientBox
            clearSearch={resetSelectedPatient}
            variant="light"
            patient={patient}
          />
        ) : (
          <AdvancedSearchBar
            onPatientClick={onSelectedPatient}
            hasSameWidthModifier
            searchBarBoxProps={{
              variant: 'light',
              hasPasteMrnOption: true,
              testHooks: {
                input: createTicketsTestSelectors.searchBar.input,
                searchButton: createTicketsTestSelectors.searchBar.searchButton
              }
            }}
            searchBarMenusProps={{
              advancedMenu: { numberOfRows: 1 },
              offset: [0, 4],
              variant: 'rounded',
              patientsMenu: { maxHeight: `calc(100vh - 184px - ${HEADER_HEIGHT * 2}px)` }
            }}
          />
        )}
      </Box>

      {patient && <PatientDetailsCard patient={patient} isEditable={false} />}
      {patient && (
        <>
          <TicketOverviewProvider
            actionCallbacks={actionCallbacks}
            getPatientSymptomOperatorTickets={ticketsStore.getPatientSymptomOperatorTickets}
          >
            {callsStore.currentEditedCall && (
              <ConnectTicketsToCallEditModal onSave={createTicketsSync} />
            )}

            <div className="tickets-overview">
              <TicketsOverview
                ticketsCluster={ticketsCluster}
                overduePatients={overduePatients}
                emptyState={null}
                showTasks={false}
                showCallbackRequests={false}
              />
            </div>
          </TicketOverviewProvider>
          {!isEmptyTickets && <StyledSeparator />}
        </>
      )}

      {patient && (
        <div className="form-wrapper">
          <TicketTypeDefaultFieldsProvider>
            <OperatorCreateTicketForm
              navigate={navigate}
              patient={patient}
              onSubmitSuccess={handleCreateTicketsSuccess}
              createFlowStartTime={selectedPatientTime}
            />
          </TicketTypeDefaultFieldsProvider>
        </div>
      )}
    </div>
  );
});

export const CreateTicketsPage = () => {
  const navigate = useNavigate();
  return (
    <PatientContextProvider>
      <CreateTickets navigate={navigate} />
    </PatientContextProvider>
  );
};

const StyledSeparator = styled('hr')(
  ({ theme }) => css`
    border-top: 1px solid ${theme.palette.natural.border};
    margin: 0 0 ${theme.spacing(40)} 0;
  `
);

export default CreateTicketsPage;
